import { useEffect, useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import { ROOT_ELEMENT_SELECTOR } from '../../consts';
import { useToasts } from '../../contexts/ToastContext';
import type { RouterProps } from '../../types';

import { ActionToast, LinkToast, Toast } from './toasts';
import { SurveyToast } from './toasts/SurveyToast';
import { isActionToast, isLinkToast, isSurveyToast } from './utils';

import './Toasts.less';

type Props = Pick<RouterProps, 'location'>;

/*
 * The Toaster is where Toasts pop out. This element exists at the bottom right of the webpage and is used as a container
 * for rendering Toasts added to the ToastContext.
 * NOTE: This should only exist *once* in the application, in the Application component.
 */
const Toaster: VoidFunctionComponent<Props> = ({ location }) => {
  const { toasts, hideToast, clearTransientToasts } = useToasts();
  const rootElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    rootElement.current = document.querySelector(ROOT_ELEMENT_SELECTOR);
  }, []);

  // This is to clear away all Toasts whenever the page changes. Especially useful for LinkToasts.
  // Survey toasts are preserved across navigation so the user can still interact with them.
  useEffect(() => {
    clearTransientToasts();
  }, [clearTransientToasts, location.pathname]);

  return (
    <div className="fossa-toast-manager">
      {toasts.map((toast) => {
        const key = `toast-${toast.id}`;
        if (isSurveyToast(toast)) {
          return (
            <SurveyToast
              key={key}
              id={toast.id}
              isVisible={toast.isVisible}
              survey={toast.survey}
              onClose={hideToast}
              onSurveySubmit={toast.onSurveySubmit}
            />
          );
        }
        if (isLinkToast(toast)) {
          return (
            <LinkToast
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...toast}
              key={key}
              onClose={hideToast}
            />
          );
        } if (isActionToast(toast)) {
          return (
            <ActionToast
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...toast}
              key={key}
              onClose={hideToast}
            />
          );
        }
        return (
          <Toast
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...toast}
            key={key}
            onClose={hideToast}
          />
        );
      })}
    </div>
  );
};

export default Toaster;
